import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import { ensureDefined } from '@e-socna/utils';
import type { Id, MissionStateId, MissionKindEnum } from '@e-socna/data-layer';

import { FolderBusinessEntityListCmp_FolderBusinessEntity_Fragment } from '../../../components/FolderBusinessEntityList/fragments/folder-business-entity.fragment';
import { useGraphQLQuery } from '../../../hooks/useGraphQLQuery.hook';
import { hasSubMissions } from '../../../app/utils/mission-kind-profile.util';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { buildSubMissionShortLabels } from '../utils/sub-mission-short-label.util';
import type { GlobalTotalOptionsSection } from '../../../components/ServiceUsages/utils/global-total-option-items.util';

import {
  MissionPage_ServiceUsages_Mission_Fragment,
  resolveMissionPricing,
} from './useServiceUsagesConsolidationAndCalculation.hook';

export const MissionPage_SubMissionsPricing_QueryDocument = graphql(`
  query MissionPage_SubMissionsPricing_Query($missionId: Id!) {
    findOneMission(id: $missionId) {
      _id
      subMissions {
        _id
        code
        folderId
        consolidation {
          title
        }
        type {
          code
          ...MissionPage_ServiceUsages_ResolvableMissionType_Fragment
        }
        ...MissionPage_ServiceUsages_Mission_Fragment
        ...FolderBusinessEntityListCmp_FolderBusinessEntity_Fragment
      }
    }
  }
`);

// A mission inheriting its pricing only has fallback groups, which are the
// ones displayed and priced.
export const pricedGroupsOf = <G,>(
  pricing: Readonly<{
    serviceUsagesGroups?: readonly G[] | null;
    fallbackServiceUsagesGroups?: readonly G[] | null;
  }>,
): readonly G[] =>
  pricing.serviceUsagesGroups ??
  pricing.fallbackServiceUsagesGroups ??
  EMPTY_ARRAY;

export const useSubMissionsPricing = (missionId: Id, kind: MissionKindEnum) => {
  const { missionStatesIdMap, quotationWorkflowsIdMap } =
    useReferencesContext();

  const { data } = useGraphQLQuery(
    MissionPage_SubMissionsPricing_QueryDocument,
    { missionId },
    { enabled: hasSubMissions(kind) },
  );

  const subMissions = (data?.findOneMission.subMissions ?? EMPTY_ARRAY).map(
    (subMission) => {
      const card = getFragmentData(
        FolderBusinessEntityListCmp_FolderBusinessEntity_Fragment,
        subMission,
      );
      const { state } = ensureDefined({
        state: missionStatesIdMap[card.stateId as MissionStateId],
      });

      return {
        _id: subMission._id,
        card: { ...card, state },
        code: subMission.code,
        compositeMissionId: missionId,
        folderId: subMission.folderId,
        title: subMission.consolidation.title,
        typeCode: subMission.type?.code,
        pricing: resolveMissionPricing({
          mission: getFragmentData(
            MissionPage_ServiceUsages_Mission_Fragment,
            subMission,
          ),
          resolvableMissionType: subMission.type,
          quotationWorkflowsIdMap,
        }),
      };
    },
  );

  const shortLabels = buildSubMissionShortLabels(subMissions);

  return {
    subMissions,
    subMissionGroups: subMissions.flatMap(({ pricing }) =>
      pricedGroupsOf(pricing),
    ),
    subMissionTotalSections: subMissions.map(
      ({ _id, pricing }, index): GlobalTotalOptionsSection => ({
        _id,
        label: shortLabels[index] ?? '',
        groupIds: pricedGroupsOf(pricing).map((group) => group._id),
      }),
    ),
  };
};
